import React, { useContext, useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FiLogOut, FiCalendar, FiMapPin, FiMail, FiHome, FiUser, FiGift, FiMoon } from "react-icons/fi";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { AuthContext, AuthContextType } from "../../../contexts/auth-context";
import { useAuth } from "../../../hooks/useAuth";
import { getMyOrders, Order, getOrderStatusLabel } from "../../../actions/order-actions";
import { getMyExtensionRequests, PendingExtensionRequest } from "../../../actions/stay-actions";
import "./Profile.scss";

const getStatusBadgeClass = (status: string): string => {
  switch (status) {
    case "Pending":
      return "upgrade-card__badge--pending";
    case "Processing":
      return "upgrade-card__badge--processing";
    case "Completed":
    case "Approved":
      return "upgrade-card__badge--completed";
    case "Failed":
    case "Rejected":
      return "upgrade-card__badge--failed";
    case "Refunded":
      return "upgrade-card__badge--refunded";
    default:
      return "";
  }
};

const getExtensionStatusLabel = (status: string): string => {
  switch (status) {
    case "Pending":
      return "Pending";
    case "Approved":
      return "Approved";
    case "Rejected":
      return "Declined";
    default:
      return status || "Unknown";
  }
};

// Combined type for upgrades list
interface UpgradeItem {
  id: string;
  type: "extension" | "order";
  title: string;
  description: string;
  status: string;
  amount: number;
  createDate: string;
}

function Profile() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, cleanUp } = useContext<AuthContextType>(AuthContext);
  const { logout } = useAuth();
  const { t } = useTranslation();

  // Check query param for initial tab (e.g., ?tab=upgrades)
  const initialTab = searchParams.get("tab") === "upgrades" ? "upgrades" : "info";
  const [activeTab, setActiveTab] = useState(initialTab);
  const [upgrades, setUpgrades] = useState<UpgradeItem[]>([]);
  const [isLoadingUpgrades, setIsLoadingUpgrades] = useState(false);
  const [upgradesError, setUpgradesError] = useState<string | null>(null);
  const [upgradesLoaded, setUpgradesLoaded] = useState(false);

  const handleLogout = () => {
    cleanUp();
    logout();
    navigate('/welcome');
  };

  const formatDate = (dateStr: string) => {
    return moment(dateStr).format("MMM D, YYYY");
  };

  // Load upgrades (extension requests + orders) when switching to Upgrades tab
  useEffect(() => {
    if (activeTab === "upgrades" && !upgradesLoaded) {
      const fetchUpgrades = async () => {
        setIsLoadingUpgrades(true);
        try {
          const [extensionsRes, ordersRes] = await Promise.all([
            getMyExtensionRequests().catch(() => ({ data: [] })),
            getMyOrders().catch(() => ({ data: [] }))
          ]);

          const extensionItems: UpgradeItem[] = (extensionsRes.data || []).map((ext: PendingExtensionRequest) => ({
            id: ext.id,
            type: "extension" as const,
            title: `${ext.numberOfNights} Night${ext.numberOfNights !== 1 ? 's' : ''} Extension`,
            description: `Extended stay until ${moment(ext.requestedDepartureTimestamp).format("MMM D, YYYY")}`,
            status: ext.status,
            amount: ext.estimatedPriceCents / 100,
            createDate: ext.createDate
          }));

          const orderItems: UpgradeItem[] = (ordersRes.data || []).map((order: Order) => ({
            id: order.id,
            type: "order" as const,
            title: order.dealTitle || "Service purchase",
            description: "",
            status: order.status,
            amount: order.amount,
            createDate: order.createDate
          }));

          // Combine and sort by date (newest first)
          const allUpgrades = [...extensionItems, ...orderItems].sort(
            (a, b) => new Date(b.createDate).getTime() - new Date(a.createDate).getTime()
          );

          setUpgrades(allUpgrades);
          setUpgradesLoaded(true);
        } catch (err: any) {
          setUpgradesError(err.response?.data || "Failed to load upgrades");
        } finally {
          setIsLoadingUpgrades(false);
        }
      };
      fetchUpgrades();
    }
  }, [activeTab, upgradesLoaded]);

  const getUpgradeIcon = (type: string) => {
    switch (type) {
      case "extension":
        return <FiMoon size={18} />;
      case "order":
        return <FiGift size={18} />;
      default:
        return <FiGift size={18} />;
    }
  };

  return (
    <div className="profile-page dark-theme">
      <div className="profile-container">
        {/* Profile Header */}
        <div className="profile-header">
          <div className="profile-header__avatar">
            {user?.name?.charAt(0)?.toUpperCase() || "G"}
          </div>
          <h2 className="profile-header__name">{user?.name || t("profile.guest")}</h2>
          {user?.email && (
            <p className="profile-header__email">
              <FiMail size={14} />
              {user.email}
            </p>
          )}
        </div>

        {/* Tab Navigation */}
        <div className="profile-tabs">
          <button
            className={`profile-tab ${activeTab === "info" ? "profile-tab--active" : ""}`}
            onClick={() => setActiveTab("info")}
          >
            <FiUser size={16} />
            {t("profile.personalInfo")}
          </button>
          <button
            className={`profile-tab ${activeTab === "upgrades" ? "profile-tab--active" : ""}`}
            onClick={() => setActiveTab("upgrades")}
          >
            <FiGift size={16} />
            {t("profile.myUpgrades")}
          </button>
        </div>

        {/* Tab Content */}
        {activeTab === "info" && (
          <>
            <div className="profile-info-card">
              <h3 className="profile-info-card__title">{t("profile.yourStay")}</h3>

              {user?.partnerName && (
                <div className="profile-info-item">
                  <div className="profile-info-item__icon">
                    <FiMapPin size={18} />
                  </div>
                  <div className="profile-info-item__content">
                    <p className="profile-info-item__label">{t("profile.property")}</p>
                    <p className="profile-info-item__value">{user.partnerName}</p>
                  </div>
                </div>
              )}

              {user?.unitName && (
                <div className="profile-info-item">
                  <div className="profile-info-item__icon">
                    <FiHome size={18} />
                  </div>
                  <div className="profile-info-item__content">
                    <p className="profile-info-item__label">{t("profile.unit")}</p>
                    <p className="profile-info-item__value">{user.unitName}</p>
                  </div>
                </div>
              )}

              {user?.checkIn && user?.checkOut && (
                <div className="profile-info-item">
                  <div className="profile-info-item__icon">
                    <FiCalendar size={18} />
                  </div>
                  <div className="profile-info-item__content">
                    <p className="profile-info-item__label">{t("profile.stayDates")}</p>
                    <p className="profile-info-item__value">
                      {formatDate(user.checkIn)} - {formatDate(user.checkOut)}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Logout Button */}
            <button className="profile-logout" onClick={handleLogout}>
              <FiLogOut size={18} />
              {t("profile.signOut")}
            </button>
          </>
        )}

        {activeTab === "upgrades" && (
          <>
            {isLoadingUpgrades ? (
              <div className="upgrades-loading">
                <div className="loading-spinner" />
                <p>{t("profile.loadingUpgrades")}</p>
              </div>
            ) : upgradesError ? (
              <div className="upgrades-error">
                <p>{upgradesError}</p>
              </div>
            ) : upgrades.length === 0 ? (
              <div className="upgrades-empty">
                <div className="upgrades-empty__icon">
                  <FiGift size={32} />
                </div>
                <p className="upgrades-empty__text">{t("profile.noUpgrades")}</p>
                <button
                  className="upgrades-empty__button"
                  onClick={() => navigate("/admin/upgrades")}
                >
                  {t("profile.browseUpgrades")}
                </button>
              </div>
            ) : (
              <div className="upgrades-list">
                {upgrades.map((upgrade) => (
                  <div key={upgrade.id} className="upgrade-card">
                    <div className="upgrade-card__icon">
                      {getUpgradeIcon(upgrade.type)}
                    </div>
                    <div className="upgrade-card__content">
                      <div className="upgrade-card__header">
                        <h4 className="upgrade-card__title">{upgrade.title}</h4>
                        <span className={`upgrade-card__badge ${getStatusBadgeClass(upgrade.status)}`}>
                          {upgrade.type === "extension"
                            ? getExtensionStatusLabel(upgrade.status)
                            : getOrderStatusLabel(upgrade.status)}
                        </span>
                      </div>
                      <div className="upgrade-card__footer">
                        <span className="upgrade-card__date">
                          <FiCalendar size={12} />
                          {moment(upgrade.createDate).format("MMM D, YYYY")}
                        </span>
                        <span className="upgrade-card__amount">
                          ${upgrade.amount.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default Profile;
